import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import howIEatService from '../services/howIEatService'
import FoodPhotoLogSection from '../components/FoodPhotoLogSection'
import { FOOD_UNITS, MEAL_TYPES, HowIEatFoodItem, HowIEatMealItem, HowIEatMealTemplate, mealItemFromFood } from '../types/howIEat'
import { templateTotals } from '../utils/howIEatHelpers'
import { useTranslation } from '../hooks/use-i18n'

const emptyItem = (): HowIEatMealItem => ({
  name: '',
  quantity: 100,
  unit: 'g',
  calories: 0,
  proteinG: 0,
  carbsG: 0,
  fatsG: 0,
})

const HowIEatLogMeal: React.FC = () => {
  const { t } = useTranslation()
  const { spaceId, mealId } = useParams<{ spaceId: string; mealId?: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = Boolean(mealId)
  const [mealType, setMealType] = useState<string>('lunch')
  const [items, setItems] = useState<HowIEatMealItem[]>([emptyItem()])
  const [notes, setNotes] = useState('')
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(isEdit)
  const [isSaving, setIsSaving] = useState(false)
  const [foodQuery, setFoodQuery] = useState('')
  const [foodResults, setFoodResults] = useState<HowIEatFoodItem[]>([])
  const [searchingFood, setSearchingFood] = useState(false)
  const [barcodeInput, setBarcodeInput] = useState('')
  const [barcodeLoading, setBarcodeLoading] = useState(false)
  const [templates, setTemplates] = useState<HowIEatMealTemplate[]>([])
  const [templateName, setTemplateName] = useState('')

  useEffect(() => {
    howIEatService.getTemplates().then(setTemplates).catch(() => {})
  }, [])

  useEffect(() => {
    const draft = (location.state as { draftItems?: HowIEatMealItem[]; draftNotes?: string } | null)?.draftItems
    const draftNotes = (location.state as { draftNotes?: string } | null)?.draftNotes
    if (draft?.length && !mealId) {
      setItems(draft)
      if (draftNotes) setNotes(draftNotes)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.pathname, location.state, mealId, navigate])

  const applyPhotoAnalysis = (analyzedItems: HowIEatMealItem[], analysisNotes?: string, previewUrl?: string) => {
    setItems(analyzedItems.length > 0 ? analyzedItems : [emptyItem()])
    if (analysisNotes) {
      setNotes((prev) => (prev.trim() ? prev : analysisNotes))
    }
    if (previewUrl) setPhotoPreview(previewUrl)
  }

  useEffect(() => {
    if (foodQuery.trim().length < 2) {
      setFoodResults([])
      return
    }
    const t = setTimeout(() => {
      setSearchingFood(true)
      howIEatService
        .searchFoods(foodQuery.trim())
        .then(setFoodResults)
        .catch(() => setFoodResults([]))
        .finally(() => setSearchingFood(false))
    }, 300)
    return () => clearTimeout(t)
  }, [foodQuery])

  const addFoodItem = (food: HowIEatFoodItem) => {
    const newItem = mealItemFromFood(food)
    setItems((prev) => {
      const empty = prev.length === 1 && !prev[0].name.trim()
      return empty ? [newItem] : [...prev, newItem]
    })
    toast.success(t('toast.foodAdded', { name: food.name }))
  }

  const lookupBarcode = async () => {
    if (!barcodeInput.trim()) return
    try {
      setBarcodeLoading(true)
      const food = await howIEatService.lookupBarcode(barcodeInput.trim())
      addFoodItem(food)
      setBarcodeInput('')
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.barcodeNotFound'))
    } finally {
      setBarcodeLoading(false)
    }
  }

  useEffect(() => {
    if (!mealId) return
    setIsLoading(true)
    howIEatService
      .getMeal(mealId)
      .then((meal) => {
        setMealType(meal.mealType)
        setItems(meal.items.length > 0 ? meal.items : [emptyItem()])
        setNotes(meal.notes || '')
      })
      .catch(() => {
        toast.error(t('toast.mealLoadFailed'))
        navigate(`/how-i-eat/${spaceId}`)
      })
      .finally(() => setIsLoading(false))
  }, [mealId, navigate, spaceId])

  const updateItem = (index: number, patch: Partial<HowIEatMealItem>) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)))
  }

  const save = async () => {
    const valid = items.filter((i) => i.name.trim())
    if (valid.length === 0) {
      toast.error(t('toast.addFoodItemRequired'))
      return
    }
    const payload = {
      mealType,
      items: valid.map((i) => ({
        name: i.name.trim(),
        quantity: Number(i.quantity) || 0,
        unit: i.unit || 'g',
        calories: Number(i.calories) || 0,
        proteinG: Number(i.proteinG) || 0,
        carbsG: Number(i.carbsG) || 0,
        fatsG: Number(i.fatsG) || 0,
      })),
      notes: notes.trim() || undefined,
    }
    try {
      setIsSaving(true)
      if (isEdit && mealId) {
        await howIEatService.updateMeal(mealId, payload)
        toast.success(t('toast.mealUpdated'))
      } else {
        await howIEatService.createMeal(payload)
        toast.success(t('toast.mealSaved'))
      }
      navigate(`/how-i-eat/${spaceId}`)
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.mealSaveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const totals = items.reduce(
    (acc, i) => ({
      cal: acc.cal + (Number(i.calories) || 0),
      p: acc.p + (Number(i.proteinG) || 0),
      c: acc.c + (Number(i.carbsG) || 0),
      f: acc.f + (Number(i.fatsG) || 0),
    }),
    { cal: 0, p: 0, c: 0, f: 0 },
  )

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-12">
      <main className="max-w-lg mx-auto px-4 py-6">
        <Link
          to={`/how-i-eat/${spaceId}`}
          className="inline-flex text-white/60 hover:text-white text-sm mb-6"
        >
          {t('common.backGeneric')}
        </Link>
        <h1 className="text-2xl font-bold text-white mb-6">{isEdit ? t('howIEat.logMeal.editTitle') : t('howIEat.logMeal.title')}</h1>

        {!isEdit && (
          <FoodPhotoLogSection onAnalyzed={applyPhotoAnalysis} previewUrl={photoPreview} />
        )}

        <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-4 mb-6 space-y-3">
          <p className="text-white/70 text-sm font-medium">{t('howIEat.logMeal.searchFoods')}</p>
          <input
            placeholder={t('howIEat.logMeal.searchPlaceholder')}
            value={foodQuery}
            onChange={(e) => setFoodQuery(e.target.value)}
            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
          />
          {searchingFood && <p className="text-white/40 text-xs">{t('howIEat.logMeal.searching')}</p>}
          {foodResults.length > 0 && (
            <div className="max-h-40 overflow-y-auto space-y-1">
              {foodResults.map((food) => (
                <button
                  key={food.id}
                  type="button"
                  onClick={() => addFoodItem(food)}
                  className="w-full text-left px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm"
                >
                  <span className="text-white">{food.name}</span>
                  {food.brand && <span className="text-white/50"> · {food.brand}</span>}
                  <span className="text-emerald-400/80 block text-xs mt-0.5">
                    {food.caloriesPer100g} cal / 100g
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <input
            placeholder={t('howIEat.logMeal.barcodePlaceholder')}
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
            />
            <button
              type="button"
              disabled={barcodeLoading}
              onClick={lookupBarcode}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
            >
              {t('howIEat.logMeal.scan')}
            </button>
          </div>
        </div>

        {templates.length > 0 && (
          <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-4 mb-6">
            <p className="text-white/70 text-sm font-medium mb-2">{t('howIEat.logMeal.templates')}</p>
            <div className="space-y-2">
              {templates.map((template) => {
                const totals = templateTotals(template.items)
                return (
                  <div
                    key={template.id}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10"
                  >
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await howIEatService.useTemplate(template.id)
                          toast.success(t('toast.templateLogged'))
                          navigate(`/how-i-eat/${spaceId}`)
                        } catch {
                          toast.error(t('toast.templateUseFailed'))
                        }
                      }}
                      className="flex-1 text-left"
                    >
                      <span className="text-white font-medium">{template.name}</span>
                      <span className="text-white/50 text-xs ml-2 capitalize">{template.mealType}</span>
                      <span className="text-emerald-400/80 text-xs block mt-0.5">
                        {t('howIEat.logMeal.templateSummary', { calories: String(Math.round(totals.calories)), count: String(template.items.length) })}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm(t('common.deleteTemplateConfirm', { name: template.name }))) return
                        try {
                          await howIEatService.deleteTemplate(template.id)
                          setTemplates((prev) => prev.filter((x) => x.id !== template.id))
                          toast.success(t('toast.templateDeleted'))
                        } catch {
                          toast.error(t('toast.templateDeleteFailed'))
                        }
                      }}
                      className="text-red-400/80 hover:text-red-400 text-xs px-2"
                    >
                      {t('howIEat.logMeal.templateDelete')}
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <p className="text-white/70 text-sm mb-2">{t('howIEat.logMeal.mealType')}</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {MEAL_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setMealType(t)}
              className={`px-4 py-2 rounded-full text-sm font-medium capitalize ${
                mealType === t
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/15'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="space-y-4 mb-6">
          {items.map((item, index) => (
            <div key={index} className="bg-[#1a1a1a] border border-white/10 rounded-xl p-4 space-y-3">
              <input
                placeholder={t('howIEat.logMeal.foodName')}
                value={item.name}
                onChange={(e) => updateItem(index, { name: e.target.value })}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  placeholder={t('howIEat.logMeal.quantity')}
                  value={item.quantity || ''}
                  onChange={(e) => updateItem(index, { quantity: +e.target.value })}
                  className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                />
                <select
                  value={item.unit}
                  onChange={(e) => updateItem(index, { unit: e.target.value })}
                  className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                >
                  {FOOD_UNITS.map((u) => (
                    <option key={u} value={u} className="bg-[#1a1a1a]">
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  placeholder={t('howIEat.logMeal.calories')}
                  value={item.calories || ''}
                  onChange={(e) => updateItem(index, { calories: +e.target.value })}
                  className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                />
                <input
                  type="number"
                  placeholder={t('howIEat.logMeal.protein')}
                  value={item.proteinG || ''}
                  onChange={(e) => updateItem(index, { proteinG: +e.target.value })}
                  className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                />
                <input
                  type="number"
                  placeholder={t('howIEat.logMeal.carbs')}
                  value={item.carbsG || ''}
                  onChange={(e) => updateItem(index, { carbsG: +e.target.value })}
                  className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                />
                <input
                  type="number"
                  placeholder={t('howIEat.logMeal.fats')}
                  value={item.fatsG || ''}
                  onChange={(e) => updateItem(index, { fatsG: +e.target.value })}
                  className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                />
              </div>
              {items.length > 1 && (
                <button
                  type="button"
                  onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                  className="text-red-400 text-sm"
                >
                  {t('howIEat.logMeal.removeItem')}
                </button>
              )}
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setItems((prev) => [...prev, emptyItem()])}
          className="text-emerald-400 text-sm font-medium mb-4"
        >
          {t('howIEat.logMeal.addAnother')}
        </button>

        <p className="text-white/60 text-sm mb-4">
          {t('howIEat.logMeal.totals', { calories: String(Math.round(totals.cal)), protein: String(Math.round(totals.p)), carbs: String(Math.round(totals.c)), fats: String(Math.round(totals.f)) })}
        </p>

        <textarea
          placeholder={t('howIEat.logMeal.notesPlaceholder')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm mb-4 min-h-[80px]"
        />

        {!isEdit && items.some((i) => i.name.trim()) && (
          <div className="flex gap-2 mb-6">
            <input
              placeholder={t('howIEat.logMeal.templateNamePlaceholder')}
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
            />
            <button
              type="button"
              onClick={async () => {
                const valid = items.filter((i) => i.name.trim())
                if (!templateName.trim() || valid.length === 0) return
                try {
                  await howIEatService.createTemplate({
                    name: templateName.trim(),
                    mealType,
                    items: valid,
                    notes: notes.trim() || undefined,
                  })
                  toast.success(t('toast.templateSaved'))
                  setTemplateName('')
                  howIEatService.getTemplates().then(setTemplates)
                } catch {
                  toast.error(t('toast.templateSaveFailed'))
                }
              }}
              className="px-4 py-2 rounded-lg border border-emerald-500/40 text-emerald-400 text-sm"
            >
              {t('howIEat.logMeal.saveTemplate')}
            </button>
          </div>
        )}

        <button
          type="button"
          disabled={isSaving}
          onClick={save}
          className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-xl font-semibold disabled:opacity-60"
        >
          {isSaving ? t('howIEat.logMeal.saving') : isEdit ? t('howIEat.logMeal.updateMeal') : t('howIEat.logMeal.saveMeal')}
        </button>
      </main>
    </div>
  )
}

export default HowIEatLogMeal
